import * as React from 'react';
import { Check, Minus } from '@phosphor-icons/react';
import { cn } from '../../lib/utils';

type CheckboxContext = 'default' | 'modal';
type CheckboxSize = 'default' | 'sm';

// The native `size` attribute means nothing on a checkbox; the prop is ours.
interface CheckboxProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size'
> {
  indicatorClassName?: string;
  label?: React.ReactNode;
  labelClassName?: string;
  wrapperClassName?: string;
  indeterminate?: boolean;
  // `modal` restyles the unchecked box and label for dark glass modal surfaces,
  // matching the Switch / RadioGroup modal context.
  context?: CheckboxContext;
  // `sm` is the compact box for dense chrome (window headers, toolbars).
  size?: CheckboxSize;
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      indicatorClassName,
      label,
      labelClassName,
      wrapperClassName,
      indeterminate = false,
      context = 'default',
      size = 'default',
      ...props
    },
    ref
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);

    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    React.useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    return (
      <label
        data-slot="checkbox-wrapper"
        data-context={context}
        data-size={size}
        className={cn(
          'relative inline-flex items-center select-none',
          size === 'sm' ? 'gap-1.5' : 'gap-2',
          !props.disabled && 'cursor-pointer',
          wrapperClassName
        )}
      >
        <input
          ref={inputRef}
          type="checkbox"
          data-slot="checkbox-input"
          className={cn(
            'peer absolute inset-0 m-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed',
            className
          )}
          {...props}
        />
        <span
          data-slot="checkbox-indicator"
          className={cn(
            'inline-flex items-center justify-center rounded-sm border border-border-subtle bg-surface-input text-transparent transition-[border-color,box-shadow,background-color,color] duration-150 ease-in-out peer-checked:border-status-success peer-checked:bg-status-success peer-checked:text-white peer-indeterminate:border-status-success peer-indeterminate:bg-status-success peer-indeterminate:text-white peer-focus-visible:outline-none peer-focus-visible:border-[var(--color-ring-default)] peer-focus-visible:shadow-[0_0_0_2px_var(--color-ring-default)] peer-disabled:opacity-50',
            size === 'sm' ? 'h-4 w-4' : 'h-5 w-5',
            context === 'modal' &&
              'border-[var(--color-neutral-0-30)] bg-[var(--color-neutral-0-10)]',
            indicatorClassName
          )}
        >
          {indeterminate ? (
            <Minus
              aria-hidden="true"
              focusable="false"
              className={cn(
                'pointer-events-none',
                size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5'
              )}
              weight="bold"
            />
          ) : (
            <Check
              aria-hidden="true"
              focusable="false"
              className={cn(
                'pointer-events-none',
                size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5'
              )}
            />
          )}
        </span>
        {label && (
          <span
            data-slot="checkbox-label"
            className={cn(
              'font-heading font-medium peer-disabled:opacity-50',
              size === 'sm' ? 'text-xs' : 'text-sm',
              context === 'modal' && 'text-[var(--color-neutral-25)]',
              labelClassName
            )}
          >
            {label}
          </span>
        )}
      </label>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export { Checkbox };
